import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Check, X, Loader2 } from 'lucide-react';
import { subscriptionApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const SubscriptionSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [attempts, setAttempts] = useState(0);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      navigate('/pricing');
      return;
    }

    const pollStatus = async () => {
      if (attempts >= 5) {
        setStatus('failed');
        return;
      }

      try {
        const result = await subscriptionApi.getStatus(sessionId);
        
        if (result.payment_status === 'paid') {
          setStatus('success');
          // Refresh auth to get updated subscription status
          await checkAuth();
          return;
        } else if (result.status === 'expired') {
          setStatus('failed');
          return;
        }

        // Keep polling
        setAttempts((prev) => prev + 1);
        setTimeout(pollStatus, 2000);
      } catch (error) {
        console.error('Status check failed:', error);
        setAttempts((prev) => prev + 1);
        setTimeout(pollStatus, 2000);
      }
    };

    pollStatus();
  }, [sessionId, attempts, navigate, checkAuth]);

  return (
    <div className="min-h-screen bg-[#030014] flex items-center justify-center px-4" data-testid="subscription-success-page">
      <div className="admin-card max-w-md w-full p-8 text-center">
        {status === 'checking' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#8B5CF6]/20 flex items-center justify-center">
              <Loader2 className="w-10 h-10 text-[#8B5CF6] animate-spin" />
            </div>
            <h1
              className="text-2xl font-bold mb-4"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Processing Payment...
            </h1>
            <p className="text-gray-400">
              Please wait while we confirm your subscription.
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#00FF9D]/20 flex items-center justify-center">
              <Check className="w-10 h-10 text-[#00FF9D]" />
            </div>
            <h1
              className="text-2xl font-bold mb-4"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Subscription Activated!
            </h1>
            <p className="text-gray-400 mb-8">
              Welcome to reframes! You now have full access to all our AI-generated films.
            </p>
            <button
              onClick={() => navigate('/browse')}
              className="btn-white"
              data-testid="start-watching-btn"
            >
              Start Watching
            </button>
          </>
        )}

        {status === 'failed' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
              <X className="w-10 h-10 text-red-400" />
            </div>
            <h1
              className="text-2xl font-bold mb-4"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Payment Issue
            </h1>
            <p className="text-gray-400 mb-8">
              There was a problem processing your payment. Please try again.
            </p>
            <button
              onClick={() => navigate('/pricing')}
              className="btn-primary"
              data-testid="try-again-btn"
            >
              Try Again
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default SubscriptionSuccessPage;
